import { Helmet } from "react-helmet-async";

const SITE = "https://www.equilybra.com.br";
const LOGO = `${SITE}/images/og-equilybra.jpg`;

const address = {
  "@type": "PostalAddress",
  streetAddress: "Rua Agostinho Gomes, 3319",
  addressLocality: "São Paulo",
  addressRegion: "SP",
  postalCode: "04206-001",
  addressCountry: "BR",
};

const geo = {
  "@type": "GeoCoordinates",
  latitude: -23.5878,
  longitude: -46.6075,
};

const openingHours = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "21:00",
  },
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Saturday"],
    opens: "08:00",
    closes: "16:00",
  },
];

const sameAs = [
  "https://www.instagram.com/equilybrainsta",
  "https://www.youtube.com/@equilybrapsicologia",
  "https://www.linkedin.com/company/equilybra-psicologia",
];

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Equilybra Psicologia",
  url: SITE,
  logo: LOGO,
  sameAs,
};

const localBusiness = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "MedicalBusiness", "ProfessionalService"],
  "@id": `${SITE}/#localbusiness`,
  name: "Equilybra Psicologia",
  url: SITE,
  image: LOGO,
  logo: LOGO,
  telephone: "+55-11-2305-0747",
  email: "contato@equilybra.com.br",
  priceRange: "$$",
  address,
  geo,
  areaServed: { "@type": "City", name: "São Paulo" },
  openingHoursSpecification: openingHours,
  sameAs,
  medicalSpecialty: ["Psychiatric", "Psychological"],
};

const website = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "Equilybra Psicologia",
  inLanguage: "pt-BR",
  publisher: { "@id": `${SITE}/#organization` },
};

const services = [
  { name: "Psicoterapia Breve", path: "/psicoterapia-breve", description: "Psicoterapia breve focal em 18 sessões para adultos." },
  { name: "Psicoterapia Infantil", path: "/psicoterapia-infantil", description: "Atendimento psicológico para crianças com abordagem lúdica." },
  { name: "Psicoterapia para Adolescentes", path: "/psicoterapia-adolescente", description: "Acolhimento e escuta especializada para adolescentes." },
  { name: "Psicoterapia para Adultos", path: "/psicoterapia-adulto", description: "Psicoterapia individual para adultos com foco em saúde mental." },
  { name: "Terapia de Casal", path: "/psicoterapia-casal", description: "Terapia de casal para fortalecer vínculos e resolver conflitos." },
  { name: "Psicoterapia da Melhor Idade", path: "/psicoterapia-melhor-idade", description: "Apoio psicológico para a terceira idade." },
  { name: "Terapia Cognitivo-Comportamental (TCC)", path: "/tcc", description: "TCC baseada em evidências para ansiedade, depressão e fobias." },
  { name: "Avaliação Neuropsicológica", path: "/avaliacao-neuropsicologica", description: "Avaliação de funções cognitivas: atenção, memória, linguagem e executivas." },
  { name: "Psicodiagnóstico", path: "/avaliacao-psicodiagnostico", description: "Investigação diagnóstica em saúde mental com laudo técnico." },
  { name: "Avaliação Pré-Cirúrgica", path: "/avaliacao-laudo-cirurgia", description: "Laudo psicológico para cirurgia bariátrica e plástica em 4 encontros." },
  { name: "Perícia Psicológica", path: "/pericia-psicologica", description: "Perícia psicológica para fins jurídicos e judiciais." },
  { name: "Orientação Vocacional", path: "/orientacao-vocacional", description: "Orientação vocacional em 5 encontros para escolha profissional." },
  { name: "Reorientação de Carreira", path: "/reorientacao-carreira", description: "Reorientação profissional para transições de carreira." },
];

const serviceSchemas = services.map((s) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: s.name,
  name: s.name,
  description: s.description,
  url: `${SITE}${s.path}`,
  provider: { "@id": `${SITE}/#localbusiness` },
  areaServed: { "@type": "City", name: "São Paulo" },
}));

const faq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "O que é psicoterapia?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Psicoterapia é um processo de cuidado em saúde mental conduzido por psicólogo, no qual o paciente é acolhido para compreender emoções, pensamentos e comportamentos, promovendo bem-estar e qualidade de vida.",
      },
    },
    {
      "@type": "Question",
      name: "Como funciona a avaliação neuropsicológica?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A avaliação neuropsicológica investiga funções cognitivas como atenção, memória, linguagem e funções executivas por meio de testes padronizados, entrevistas e devolutiva com laudo técnico.",
      },
    },
    {
      "@type": "Question",
      name: "A Equilybra faz atendimento online?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sim. Realizamos atendimento online por videochamada para psicoterapia, com a mesma qualidade do atendimento presencial, em todo o Brasil.",
      },
    },
    {
      "@type": "Question",
      name: "Qual o endereço da clínica?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Estamos na Rua Agostinho Gomes, 3319 — Ipiranga, São Paulo — SP, próximo ao metrô Sacomã.",
      },
    },
    {
      "@type": "Question",
      name: "Quais convênios são aceitos?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Atendemos diversos convênios e também particular. Entre em contato pelo WhatsApp (11) 97697-4905 para confirmar a cobertura do seu plano.",
      },
    },
    {
      "@type": "Question",
      name: "Quantas sessões dura a psicoterapia breve?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A psicoterapia breve focal da Equilybra tem duração padrão de 18 sessões semanais, com foco em uma demanda específica trazida pelo paciente.",
      },
    },
  ],
};

const HomeStructuredData = () => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(organization)}</script>
    <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
    <script type="application/ld+json">{JSON.stringify(website)}</script>
    {serviceSchemas.map((s, i) => (
      <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
    ))}
    <script type="application/ld+json">{JSON.stringify(faq)}</script>
  </Helmet>
);

export default HomeStructuredData;
